import { Controller } from "@hotwired/stimulus"

/**
 * Form State Controller - Tracks dirty state and prevents data loss
 * Monitors form changes and warns users about unsaved changes
 */
export default class extends Controller {
  static targets = ["form"]

  static values = {
    dirty: { type: Boolean, default: false },
    originalData: { type: Object, default: {} }
  }

  connect() {
    console.log('[form-state] Controller connected')

    // Store original form data
    this.originalDataValue = this.currentFormData()

    // Listen for beforeunload (browser close/refresh)
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this)
    window.addEventListener('beforeunload', this.boundHandleBeforeUnload)

    // Listen for Turbo navigation
    this.boundHandleTurboVisit = this.handleTurboVisit.bind(this)
    document.addEventListener('turbo:before-visit', this.boundHandleTurboVisit)
  }

  disconnect() {
    console.log('[form-state] Controller disconnected')

    // Remove event listeners
    window.removeEventListener('beforeunload', this.boundHandleBeforeUnload)
    document.removeEventListener('turbo:before-visit', this.boundHandleTurboVisit)

    // Clean up
    this.boundHandleBeforeUnload = null
    this.boundHandleTurboVisit = null
  }

  /**
   * Mark form as dirty (has unsaved changes)
   */
  markDirty() {
    if (!this.dirtyValue) {
      console.log('[form-state] Form marked as dirty')
      this.dirtyValue = true
    }
  }

  /**
   * Mark form as clean (all changes saved)
   */
  markClean() {
    if (this.dirtyValue) {
      console.log('[form-state] Form marked as clean')
      this.dirtyValue = false
      this.originalDataValue = this.currentFormData()
    }
  }

  /**
   * Check if form data has changed
   * Called on form change events
   */
  checkDirty() {
    const current = this.currentFormData()
    const original = this.originalDataValue

    // Compare current and original data
    const hasChanged = JSON.stringify(current) !== JSON.stringify(original)

    if (hasChanged && !this.dirtyValue) {
      this.markDirty()
    } else if (!hasChanged && this.dirtyValue) {
      this.markClean()
    }
  }

  /**
   * Handle browser close/refresh
   * @param {BeforeUnloadEvent} event - Before unload event
   */
  handleBeforeUnload(event) {
    if (this.dirtyValue) {
      console.log('[form-state] Preventing unload due to unsaved changes')

      // Standard way to show confirmation dialog
      event.preventDefault()
      event.returnValue = '' // Chrome requires returnValue to be set

      return ''
    }
  }

  /**
   * Handle Turbo navigation
   * @param {Event} event - Turbo visit event
   */
  handleTurboVisit(event) {
    if (this.dirtyValue) {
      const message = 'You have unsaved changes. Are you sure you want to leave?'

      if (!confirm(message)) {
        console.log('[form-state] Preventing Turbo navigation due to unsaved changes')
        event.preventDefault()
      }
    }
  }

  /**
   * Get the form element (either via target or direct attachment)
   * @returns {HTMLFormElement|null} The form element
   */
  getFormElement() {
    // First, check if we have a form target
    if (this.hasFormTarget) {
      return this.formTarget
    }

    // Fallback: check if controller is attached directly to a form
    if (this.element instanceof HTMLFormElement) {
      return this.element
    }

    // Last resort: find a form within the element
    const formWithinElement = this.element.querySelector('form')
    if (formWithinElement) {
      return formWithinElement
    }

    return null
  }

  /**
   * Get current form data as object
   * @returns {Object} Form data
   */
  currentFormData() {
    const formElement = this.getFormElement()

    if (!formElement) {
      console.error('[form-state] No form element found. Either attach controller to a <form> element, define a form target, or ensure a form exists within the controller element.')
      console.error('[form-state] Current element:', this.element.tagName)
      return {}
    }

    const formData = new FormData(formElement)
    const data = {}

    for (const [key, value] of formData.entries()) {
      // Handle multiple values for same key (e.g., checkboxes)
      if (data[key]) {
        if (Array.isArray(data[key])) {
          data[key].push(value)
        } else {
          data[key] = [data[key], value]
        }
      } else {
        data[key] = value
      }
    }

    return data
  }

  /**
   * Check if form has unsaved changes
   * @returns {boolean} True if dirty
   */
  isDirty() {
    return this.dirtyValue
  }

  /**
   * Reset form to original state
   */
  reset() {
    console.log('[form-state] Resetting form to original state')

    const formElement = this.getFormElement()
    if (!formElement) {
      console.error('[form-state] Cannot reset: no form element found')
      return
    }

    const original = this.originalDataValue

    // Restore original values
    Object.entries(original).forEach(([name, value]) => {
      const field = formElement.querySelector(`[name="${name}"]`)
      if (field) {
        if (field.type === 'checkbox') {
          field.checked = value === 'on' || value === '1' || value === true
        } else if (field.type === 'radio') {
          field.checked = field.value === value
        } else {
          field.value = value
        }
      }
    })

    this.markClean()
  }
}
